import { useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Shield, CheckCircle, Download, Copy, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { extractError } from "../lib/errors";

// ---- Step indicators ----
const StepDot = ({ label, active, done }) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors
        ${done ? "bg-green-500 border-green-500 text-white"
          : active ? "border-primary text-primary bg-primary/10"
          : "border-muted text-muted-foreground"}`}
    >
      {done ? <CheckCircle className="w-4 h-4" /> : label}
    </div>
  </div>
);

const StepBar = ({ step }) => {
  const { t } = useI18n();
  const labels = [
    t("auth.2fa.setup.step_label_1"),
    t("auth.2fa.setup.step_label_2"),
    t("auth.2fa.setup.step_label_3"),
  ];
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {labels.map((label, i) => (
        <div key={i} className="flex items-center gap-2">
          <StepDot label={i + 1} active={step === i} done={step > i} />
          {i < labels.length - 1 && (
            <div className={`w-8 h-0.5 ${step > i ? "bg-green-500" : "bg-muted"}`} />
          )}
        </div>
      ))}
    </div>
  );
};

// ---- Helpers ----
function formatSecret(secret) {
  // "JBSWYXXXXX..." → "JBSW YXXX XXXX ..."
  return (secret || "").match(/.{1,4}/g)?.join(" ") || secret;
}

function downloadRecoveryCodes(codes) {
  const text = [
    "ComplianceOS — Recovery Codes",
    "=".repeat(32),
    "Store these in a secure location.",
    "Each code can only be used once.",
    "",
    ...codes,
    "",
    `Generated: ${new Date().toISOString()}`,
  ].join("\n");
  const blob = new Blob([text], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "complianceos-recovery-codes.txt";
  a.click();
  URL.revokeObjectURL(url);
}

// ---- Main component ----
const TwoFactorSetupPage = () => {
  const { t } = useI18n();
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Si viene de un registro nuevo, mostrar botón "Skip for now"
  const isOnboarding = searchParams.get("setup") === "onboarding";

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // Step 0 data
  const [setupData, setSetupData] = useState(null); // {secret_b32, qr_svg, provisioning_uri}
  // Step 1 data
  const [verifyCode, setVerifyCode] = useState("");
  // Step 2 data
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [savedConfirm, setSavedConfirm] = useState(false);

  // Disable 2FA state
  const [disablePassword, setDisablePassword] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const is2faActive = user?.totp_enabled === true;

  // ---- Step 0: start setup ----
  const handleBeginSetup = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/setup`);
      setSetupData(res.data);
      setStep(1);
    } catch (err) {
      toast.error(extractError(err, t("auth.2fa.setup.error_setup")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // ---- Step 1: verify code ----
  const handleVerifyCode = useCallback(async (e) => {
    e.preventDefault();
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error(t("auth.2fa.setup.code_required"));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify-setup`, { code: verifyCode });
      setRecoveryCodes(res.data.recovery_codes || []);
      setStep(2);
      toast.success(t("auth.2fa.setup.activated"));
    } catch (err) {
      toast.error(extractError(err, t("auth.2fa.setup.invalid_code")));
    } finally {
      setLoading(false);
    }
  }, [verifyCode, t]);

  // ---- Step 2: finish ----
  const handleFinish = useCallback(async () => {
    // Refresh user so totp_enabled reflects true
    await checkAuth();
    navigate("/dashboard");
    toast.success(t("auth.2fa.setup.finish_success"));
  }, [checkAuth, navigate, t]);

  // ---- Disable 2FA ----
  const handleDisable = useCallback(async (e) => {
    e.preventDefault();
    if (!disablePassword || !disableCode) {
      toast.error(t("auth.2fa.disable.fields_required"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, {
        password: disablePassword,
        code: disableCode,
      });
      await checkAuth();
      toast.success(t("auth.2fa.disable.success"));
      setDisablePassword("");
      setDisableCode("");
    } catch (err) {
      toast.error(extractError(err, t("auth.2fa.disable.error")));
    } finally {
      setLoading(false);
    }
  }, [disablePassword, disableCode, checkAuth, t]);

  // ---- Render: 2FA already active ----
  if (is2faActive) {
    return (
      <div className="max-w-lg mx-auto p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-green-500" />
          <h1 className="text-2xl font-bold">{t("auth.2fa.setup.title")}</h1>
        </div>

        <Card className="border-green-500/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-500">
              <CheckCircle className="w-5 h-5" />
              {t("auth.2fa.status.active")}
            </CardTitle>
            <CardDescription>{t("auth.2fa.status.active_desc")}</CardDescription>
          </CardHeader>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-destructive">{t("auth.2fa.disable.title")}</CardTitle>
            <CardDescription>{t("auth.2fa.disable.warning")}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleDisable} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="dis-pwd">{t("auth.2fa.disable.password_label")}</Label>
                <Input
                  id="dis-pwd"
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                  placeholder={t("auth.2fa.disable.password_placeholder")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dis-code">{t("auth.2fa.disable.code_label")}</Label>
                <Input
                  id="dis-code"
                  type="text"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  placeholder={t("auth.2fa.disable.code_placeholder")}
                  maxLength={17}
                />
                <p className="text-xs text-muted-foreground">
                  {t("auth.2fa.disable.code_hint")}
                </p>
              </div>
              <Button
                type="submit"
                variant="destructive"
                className="w-full"
                disabled={loading}
              >
                {loading ? t("common.loading") : t("auth.2fa.disable.button")}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Render: wizard setup ----
  return (
    <div className="max-w-lg mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <Shield className="w-6 h-6 text-primary" />
        <h1 className="text-2xl font-bold">{t("auth.2fa.setup.title")}</h1>
      </div>

      <StepBar step={step} />

      {/* Step 0: intro */}
      {step === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("auth.2fa.setup.intro_title")}</CardTitle>
            <CardDescription>{t("auth.2fa.setup.intro_desc")}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-6">
              {t("auth.2fa.setup.step1")}
            </p>
            <Button onClick={handleBeginSetup} disabled={loading} className="w-full">
              {loading ? t("common.loading") : t("auth.2fa.setup.begin")}
              {!loading && <ChevronRight className="ml-2 w-4 h-4" />}
            </Button>
            {isOnboarding && (
              <Button
                type="button"
                variant="ghost"
                className="w-full mt-2 text-muted-foreground"
                onClick={() => navigate("/dashboard")}
              >
                {t("auth.2fa.banner.dismiss")}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 1: QR + secret */}
      {step === 1 && setupData && (
        <Card>
          <CardHeader>
            <CardTitle>{t("auth.2fa.setup.scan_title")}</CardTitle>
            <CardDescription>{t("auth.2fa.setup.step1")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* QR SVG inline — envuelto en bg-white para contraste dark-mode */}
            <div
              className="bg-white p-4 rounded-lg flex items-center justify-center mx-auto max-w-[220px]"
              dangerouslySetInnerHTML={{ __html: setupData.qr_svg }}
            />

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                {t("auth.2fa.setup.manual_secret")}
              </p>
              <code className="block bg-muted rounded p-2 text-xs font-mono tracking-wider break-all">
                {formatSecret(setupData.secret_b32)}
              </code>
            </div>

            <form onSubmit={handleVerifyCode} className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="totp-code">{t("auth.2fa.setup.step2")}</Label>
                <Input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  placeholder="000000"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ""))}
                  autoFocus
                  autoComplete="one-time-code"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full">
                {loading ? t("common.loading") : t("auth.2fa.setup.verify_button")}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 2: recovery codes */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("auth.2fa.setup.recovery_title")}</CardTitle>
            <CardDescription>{t("auth.2fa.setup.step3")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted rounded-lg p-4 grid grid-cols-2 gap-2">
              {recoveryCodes.map((code) => (
                <code key={code} className="text-sm font-mono text-center py-1 px-2 bg-background rounded">
                  {code}
                </code>
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => downloadRecoveryCodes(recoveryCodes)}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                {t("auth.2fa.setup.download_codes")}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(recoveryCodes.join("\n"));
                  toast.success(t("auth.2fa.setup.codes_copied"));
                }}
                className="flex-1"
              >
                <Copy className="w-4 h-4 mr-2" />
                {t("common.copy")}
              </Button>
            </div>

            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                className="mt-0.5 accent-primary"
                checked={savedConfirm}
                onChange={(e) => setSavedConfirm(e.target.checked)}
              />
              <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                {t("auth.2fa.setup.confirm_saved")}
              </span>
            </label>

            <Button
              onClick={handleFinish}
              disabled={!savedConfirm || loading}
              className="w-full"
            >
              {t("auth.2fa.setup.finish")}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TwoFactorSetupPage;
